const DEFAULT_DOC_NAME_BASE = 'New Doc';
const DEFAULT_CHAT_NAME_BASE = 'New Chat';

export const RENAME_MODE_AUTO = 'auto';
export const RENAME_MODE_PROMPT = 'prompt';
export const RENAME_MODE_OFF = 'off';

const escapeRegex = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeDocType = (docType = null) => {
  return String(docType || '').trim().toLowerCase() === 'chat' ? 'chat' : null;
};

const normalizeWhitespace = (value = '') => {
  return String(value || '').replace(/\s+/g, ' ').trim();
};

const replaceControlChars = (value = '') => {
  return String(value || '')
    .split('')
    .map((ch) => (ch.charCodeAt(0) < 32 ? ' ' : ch))
    .join('');
};

const capitalizeBoundaryPattern = /^[A-Z0-9]/;

export const truncateRenameToWordBoundary = (value, options = {}) => {
  const softLength = Number(options?.softLength || 30) || 30;
  const normalized = normalizeWhitespace(value);
  if (!normalized || normalized.length <= softLength) return normalized;

  const words = normalized.split(' ').filter(Boolean);
  let consumedLength = 0;
  let fallbackEnd = 0;

  for (let index = 0; index < words.length; index += 1) {
    const word = words[index];
    consumedLength += (index === 0 ? 0 : 1) + word.length;
    if (consumedLength <= softLength) {
      fallbackEnd = consumedLength;
      continue;
    }

    if (capitalizeBoundaryPattern.test(word)) {
      return normalized.slice(0, consumedLength).trim();
    }
  }

  return fallbackEnd > 0 ? normalized.slice(0, fallbackEnd).trim() : normalized;
};

export const getUntitledBaseName = (docType = null) => {
  return normalizeDocType(docType) === 'chat' ? DEFAULT_CHAT_NAME_BASE : DEFAULT_DOC_NAME_BASE;
};

export const getRenameModePreferenceKey = (docType = null) => {
  return normalizeDocType(docType) === 'chat' ? 'chatRenameMode' : 'docRenameMode';
};

export const getDefaultRenameMode = (docType = null) => {
  return normalizeDocType(docType) === 'chat' ? RENAME_MODE_AUTO : RENAME_MODE_PROMPT;
};

export const normalizeRenameMode = (value, docType = null) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === RENAME_MODE_AUTO || normalized === RENAME_MODE_PROMPT || normalized === RENAME_MODE_OFF) {
    return normalized;
  }
  return getDefaultRenameMode(docType);
};

export const isUntitledDefaultName = (name, docType = null) => {
  const trimmed = normalizeWhitespace(name);
  if (!trimmed) return false;
  const base = getUntitledBaseName(docType);
  const indexedNameRe = new RegExp(`^${escapeRegex(base)}(?:\\s+[1-9]\\d*)?$`);
  return indexedNameRe.test(trimmed);
};

export const isAutoRenameEligibleName = (name, docType = null, renameMode = null) => {
  const mode = renameMode == null ? null : normalizeRenameMode(renameMode, docType);
  if (mode === RENAME_MODE_OFF) return false;
  return isUntitledDefaultName(name, docType);
};

export const sanitizeSuggestedRename = (value, options = {}) => {
  const {
    maxLength = 120,
    fallback = '',
    softLength = 30
  } = options || {};

  const normalized = normalizeWhitespace(
    replaceControlChars(
      String(value || '')
      .trim()
      .replace(/^['"`]+|['"`]+$/g, '')
    )
  );

  const candidate = normalized || normalizeWhitespace(fallback);
  if (!candidate) return '';
  const truncated = truncateRenameToWordBoundary(candidate, { softLength });
  if (truncated.length <= maxLength) return truncated;
  return truncated.slice(0, Math.max(1, maxLength)).trim();
};

export const getNodeTextContent = (node) => {
  if (!node || typeof node !== 'object') return '';
  if (typeof node.text === 'string') return node.text;
  if (!Array.isArray(node.content)) return '';
  return node.content.map(getNodeTextContent).join('');
};

const asNodeList = (node) => {
  if (!node || typeof node !== 'object') return [];
  return Array.isArray(node.content) ? node.content : [node];
};

const getFirstChunkNodes = (nodes = []) => {
  const chunkNodes = nodes.filter((node) => String(node?.type || '').trim() === 'chunk');
  if (chunkNodes.length > 0) {
    return asNodeList(chunkNodes[0]);
  }
  return nodes;
};

const findFirstNodeByPredicate = (nodes, predicate) => {
  for (const node of nodes) {
    if (!node || typeof node !== 'object') continue;
    if (predicate(node)) return node;
    const children = Array.isArray(node.content) ? node.content : [];
    const nested = findFirstNodeByPredicate(children, predicate);
    if (nested) return nested;
  }
  return null;
};

const findStrongestHeadingNode = (nodes, best = null) => {
  let nextBest = best;
  for (const node of nodes) {
    if (!node || typeof node !== 'object') continue;

    if (String(node?.type || '').trim() === 'heading') {
      const text = getNodeTextContent(node);
      const level = Number(node?.attrs?.level);
      const normalizedLevel = Number.isFinite(level) && level > 0 ? level : 6;
      if (text) {
        const bestLevel = Number(nextBest?.level || 99);
        if (!nextBest || normalizedLevel < bestLevel) {
          nextBest = { node, level: normalizedLevel };
        }
      }
    }

    const children = Array.isArray(node.content) ? node.content : [];
    if (children.length > 0) {
      nextBest = findStrongestHeadingNode(children, nextBest);
    }
  }

  return nextBest;
};

export const extractHeadingRenameCandidate = (content, options = {}) => {
  const maxLength = Number(options?.maxLength || 120) || 120;
  const docJson = content?.json && typeof content.json === 'object' ? content.json : content;
  const nodes = Array.isArray(docJson?.content) ? docJson.content : [];

  const firstChunkNodes = getFirstChunkNodes(nodes);
  if (!Array.isArray(firstChunkNodes) || firstChunkNodes.length === 0) return '';
  const strongestHeading = findStrongestHeadingNode(firstChunkNodes);
  if (strongestHeading?.node) {
    return sanitizeSuggestedRename(getNodeTextContent(strongestHeading.node), { maxLength });
  }

  const firstTextNode = findFirstNodeByPredicate(firstChunkNodes, (node) => Boolean(getNodeTextContent(node)));
  if (firstTextNode) {
    return sanitizeSuggestedRename(getNodeTextContent(firstTextNode), { maxLength });
  }

  return '';
};

export const shouldApplySuggestedRename = ({
  currentName,
  docType = null,
  renameMode = null,
  suggestedName = ''
} = {}) => {
  const mode = normalizeRenameMode(renameMode, docType);
  const sanitizedSuggestion = sanitizeSuggestedRename(suggestedName);
  if (mode === RENAME_MODE_OFF || !sanitizedSuggestion) return false;
  if (!isAutoRenameEligibleName(currentName, docType, mode)) return false;
  return normalizeWhitespace(currentName) !== sanitizedSuggestion;
};
